import type { EligibilityCheckRequest } from '@galapagos/shared';
import { clearSamlSession, readSamlSession, updateSamlSession } from '@/services/saml-session';
import { SignupRequestError, type SignupApi } from '../services/signup-api/signup-api';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { resetQuietEligibilityCheck, startQuietEligibilityCheck } from './eligibility-poller';

type EligibilityApi = Pick<SignupApi, 'submitEligibility' | 'getEligibilityStatus'>;

interface SsoCheckInput {
  dateOfBirth?: string;
  /** The regular check for this employer, when the caller has the answers for it. */
  fallback?: () => EligibilityCheckRequest | null;
}

/** The server wants a birth date the roster lacks; the birth-date step sends one. */
const DOB_REQUIRED = 'saml_dob_required';

// Module state, like the poller's: the check outlives the step that started
// it. `inFlightSession` is the sealed session whose check is out now, and
// `latest` is what the most recent caller asked for, so a check still in
// flight when a later step arrives can answer that step if it fails.
let inFlightSession: string | null = null;
let latest: SsoCheckInput = {};

/**
 * Whether the server refused the sealed session itself (expired, or not bound
 * to this account), so retrying it can never work. A missing birth date is
 * not a refusal: the same session goes again with one.
 */
function sessionRefused(error: unknown): boolean {
  return (
    error instanceof SignupRequestError && error.httpStatus === 400 && error.code !== DOB_REQUIRED
  );
}

function send(api: EligibilityApi, session: string, input: SsoCheckInput): void {
  inFlightSession = session;
  const epoch = useSignupFlowStore.getState().flowEpoch;
  // An answer only speaks for the flow and the sealed session it was sent
  // for: a member who started over, or landed from SSO again, has a new one.
  const stillCurrent = () =>
    useSignupFlowStore.getState().flowEpoch === epoch && readSamlSession()?.session === session;
  // Marked sent only once the server accepted it: a reload that interrupts
  // the request must leave the birth-date step free to send it again.
  const acknowledged: EligibilityApi = {
    ...api,
    submitEligibility: (request) =>
      api.submitEligibility(request).then((response) => {
        if (stillCurrent()) updateSamlSession({ checkSent: true });
        return response;
      }),
  };
  const request: EligibilityCheckRequest = {
    samlSession: session,
    ...(input.dateOfBirth === undefined ? {} : { dateOfBirth: input.dateOfBirth }),
  };
  const sent = startQuietEligibilityCheck(acknowledged, request, (error) => {
    if (inFlightSession !== session) return;
    inFlightSession = null;
    if (!stillCurrent()) return;
    resetQuietEligibilityCheck();
    if (sessionRefused(error)) {
      clearSamlSession();
      const regular = latest.fallback?.();
      if (regular) startQuietEligibilityCheck(api, regular);
      return;
    }
    // A later step asked while this was in flight, and no step after it will
    // ask again, so its answers go now.
    if (latest !== input) send(api, session, latest);
  });
  // The shared checker refuses a repeat this page already sent; nothing is in
  // flight then, and no failure will ever arrive to clear the flag.
  if (!sent) inFlightSession = null;
}

/**
 * Starts an employer SSO member's eligibility check from the sealed roster
 * session rather than their typed answers. Returns false when there is no
 * session, and the caller sends the regular check instead.
 *
 * The check goes once: right after sign-in when the roster has a birth date
 * (the session lives for minutes, so it is used while fresh), otherwise from
 * the birth-date step with the date the member entered (the server prefers
 * the roster's). A refused session is dropped and the latest caller's
 * fallback goes instead. Any other failure leaves the session for the
 * birth-date step, or, when that step already asked, sends again at once.
 */
export function startSsoEligibilityCheck(api: EligibilityApi, input: SsoCheckInput = {}): boolean {
  const stored = readSamlSession();
  if (stored === null) return false;
  latest = input;
  // In flight, or already sent before a reload: the first check answers.
  if (inFlightSession === stored.session || stored.checkSent === true) return true;
  // A check from an earlier sealed session is superseded: stop following it,
  // so the shared checker takes this session's check.
  if (inFlightSession !== null) {
    inFlightSession = null;
    resetQuietEligibilityCheck();
  }
  send(api, stored.session, input);
  return true;
}

/** Forgets the in-flight check and the latest caller (a fresh flow, or a test). */
export function resetSsoEligibilityCheck(): void {
  inFlightSession = null;
  latest = {};
}

useSignupFlowStore.subscribe((state, previous) => {
  if (state.flowEpoch !== previous.flowEpoch) resetSsoEligibilityCheck();
});
